import { type ReactElement } from 'react';

import { Button, Center, Heading, Stack, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { Link } from '@/components/Link';
import { Logo } from '@/components/Logo';

import { Illustration403 } from './Illustration403';
import { Illustration404 } from './Illustration404';
import { IllustrationDefault } from './IllustrationDefault';

const SupportedErrors: Record<
  'default' | 403 | 404,
  { illustration?: ReactElement }
> = {
  default: { illustration: <IllustrationDefault /> },
  403: { illustration: <Illustration403 /> },
  404: { illustration: <Illustration404 /> },
};

export const ErrorPage = ({ errorCode }: { errorCode?: number }) => {
  const errorType =
    errorCode && errorCode in SupportedErrors
      ? (errorCode as keyof typeof SupportedErrors)
      : 'default';
  const illustration =
    SupportedErrors[errorType].illustration ??
    SupportedErrors.default.illustration ??
    null;

  const { t } = useTranslation(['common', 'auth']);

  return (
    <Center flex="1" p="8">
      <Stack
        direction={{ base: 'column-reverse', md: 'row' }}
        align="center"
        spacing={4}
      >
        {illustration}
        <Stack
          textAlign={{ base: 'center', md: 'left' }}
          alignItems={{ base: 'center', md: 'flex-start' }}
        >
          <Link to="/">
            <Logo my={4} />
          </Link>
          <Heading>{t(`common:error.page.title.${errorType}`)}</Heading>
          <Text>{t(`common:error.page.message.${errorType}`)}</Text>
          {errorType === 403 && (
            <Link to="/logout" mt={2}>
              <Button size="sm" variant="@primary">
                <Text fontSize="sm">
                  {t('auth:admin.layout.accountMenu.logout')}
                </Text>
              </Button>
            </Link>
          )}
        </Stack>
      </Stack>
    </Center>
  );
};
